import { supabase } from '../lib/supabase';

export type PriceType = 'shared_energy_price' | 'prosumer_buy_price' | 'prosumer_sell_price';

export interface TariffOverride {
  id: string;
  participant_id: string;
  price_type: PriceType;
  new_price: number;
  start_date: string;
  end_date: string | null;
  created_at: string;
}

/**
 * Fetch all tariff overrides for a participant.
 */
export async function fetchTariffOverrides(participantId: string): Promise<TariffOverride[]> {
  const { data, error } = await supabase
    .from('participant_tariff_overrides')
    .select('*')
    .eq('participant_id', participantId)
    .order('start_date', { ascending: false });

  if (error) {
    console.error('Error fetching tariff overrides:', error);
    return [];
  }
  return (data || []) as TariffOverride[];
}

/**
 * Fetch all tariff overrides for multiple participants in one query.
 * Returns a map keyed by participant_id.
 */
export async function fetchTariffOverridesBatch(
  participantIds: string[]
): Promise<Record<string, TariffOverride[]>> {
  if (participantIds.length === 0) return {};

  const { data, error } = await supabase
    .from('participant_tariff_overrides')
    .select('*')
    .in('participant_id', participantIds)
    .order('start_date', { ascending: false });

  if (error) {
    console.error('Error fetching tariff overrides batch:', error);
    return {};
  }

  const map: Record<string, TariffOverride[]> = {};
  for (const row of data || []) {
    if (!map[row.participant_id]) map[row.participant_id] = [];
    map[row.participant_id].push(row as TariffOverride);
  }
  return map;
}

/**
 * Resolve the effective price for a given date.
 *
 * Algorithm:
 * 1. Check overrides that cover the target date (start_date <= date <= end_date, or end_date is null).
 *    If multiple match, pick the one with the latest start_date.
 * 2. If no override covers the date, return the baseline price from the participant row.
 *
 * @param baselinePrice - The original price from the participant record
 * @param overrides - All overrides for this participant + price_type, sorted by start_date DESC
 * @param dateStr - The date to resolve for, in 'YYYY-MM-DD' format
 */
export function resolvePriceForDate(
  baselinePrice: number | null | undefined,
  overrides: TariffOverride[],
  dateStr: string
): number {
  const base = Number(baselinePrice ?? 0);

  if (!overrides || overrides.length === 0) return base;

  const matching = overrides.filter((o) => {
    if (o.start_date > dateStr) return false;
    if (o.end_date && o.end_date < dateStr) return false;
    return true;
  });

  if (matching.length === 0) return base;

  // Pick the override with the latest start_date (most specific)
  matching.sort((a, b) => b.start_date.localeCompare(a.start_date));
  return Number(matching[0].new_price);
}

/**
 * Resolve the effective price for a given month (YYYY-MM).
 * Uses the first day of the month as the reference date.
 */
export function resolvePriceForMonth(
  baselinePrice: number | null | undefined,
  overrides: TariffOverride[],
  monthKey: string // 'YYYY-MM'
): number {
  return resolvePriceForDate(baselinePrice, overrides, `${monthKey}-01`);
}

/**
 * Insert a new tariff override.
 */
export async function createTariffOverride(
  participantId: string,
  priceType: PriceType,
  newPrice: number,
  startDate: string,
  endDate: string | null
): Promise<TariffOverride | null> {
  const { data: session } = await supabase.auth.getSession();
  const createdBy = session?.user?.id || null;

  const { data, error } = await supabase
    .from('participant_tariff_overrides')
    .insert({
      participant_id: participantId,
      price_type: priceType,
      new_price: newPrice,
      start_date: startDate,
      end_date: endDate,
      created_by: createdBy,
    })
    .select('*')
    .single();

  if (error) {
    console.error('Error creating tariff override:', error);
    throw error;
  }

  return data as TariffOverride;
}

/**
 * Delete a tariff override.
 */
export async function deleteTariffOverride(overrideId: string): Promise<void> {
  const { error } = await supabase
    .from('participant_tariff_overrides')
    .delete()
    .eq('id', overrideId);

  if (error) throw error;
}
